"use client";

import classnames from "classnames";

import type { AuthState } from "./types";
import styles from "./sidebar.module.scss";

interface SidebarAuthProps {
  auth: AuthState;
  collapsed: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
  onCollapseToggle: () => void;
}

function SyncDot({ status }: { status: "synced" | "syncing" | "offline" }) {
  const className = classnames(styles.syncDot, styles[`syncDot_${status}`]);
  const label = status === "synced" ? "Synced" : status === "syncing" ? "Syncing" : "Offline";
  return <span className={className} role="status" aria-label={label} title={label} />;
}

export function SidebarAuth({
  auth,
  collapsed,
  onSignIn,
  onSignOut,
  onCollapseToggle,
}: SidebarAuthProps) {
  if (auth.status === "signed-out") {
    return (
      <div className={styles.authSection}>
        {/* Expanded: sign-in button (determines section height) */}
        <button
          type="button"
          className={styles.signInBtn}
          onClick={onSignIn}
          tabIndex={collapsed ? -1 : undefined}
        >
          <span className={styles.signInLabel}>Sign in to sync</span>
          <span className={styles.signInSub}>Access across devices</span>
        </button>

        {/* Collapsed: icon button (absolute overlay) */}
        <button
          type="button"
          className={styles.authCollapsedBtn}
          onClick={() => {
            onCollapseToggle();
            onSignIn();
          }}
          aria-label="Sign in to sync"
          title="Sign in to sync"
          tabIndex={!collapsed ? -1 : undefined}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </button>
      </div>
    );
  }

  // Signed in — dual render
  return (
    <div className={styles.authSection}>
      {/* Expanded: full auth row (determines section height) */}
      <div className={classnames(styles.authRow, collapsed && styles.authHidden)}>
        <span className={styles.avatar}>{auth.email.charAt(0).toUpperCase()}</span>
        <div className={styles.authInfo}>
          <span className={styles.authEmail}>{auth.email}</span>
          <button
            type="button"
            className={styles.signOutLink}
            onClick={onSignOut}
            tabIndex={collapsed ? -1 : undefined}
          >
            Sign out
          </button>
        </div>
        <SyncDot status={auth.syncStatus} />
      </div>

      {/* Collapsed: avatar button (absolute overlay) */}
      <button
        type="button"
        className={classnames(styles.avatarBtn, !collapsed && styles.authHidden)}
        onClick={onCollapseToggle}
        title={auth.email}
        aria-label={`${auth.email} — expand for options`}
        tabIndex={!collapsed ? -1 : undefined}
      >
        <span className={styles.avatar}>{auth.email.charAt(0).toUpperCase()}</span>
        <SyncDot status={auth.syncStatus} />
      </button>
    </div>
  );
}
